import axios from 'axios';
import authService from './authService';

// Ensure we have the axiosInstance properly defined
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Create an axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to include auth token
axiosInstance.interceptors.request.use(
  config => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Add debugging for auth token
const getAuthToken = () => {
  // Try from localStorage first
  let token = localStorage.getItem('token');
  
  // If not in localStorage, try from sessionStorage
  if (!token) {
    token = sessionStorage.getItem('token');
  }
  
  // Log details without exposing the full token
  if (token) {
    console.log(`Auth token found: ${token.substring(0, 5)}...${token.substring(token.length - 5)}`);
  } else {
    console.log('No auth token found in storage');
  }
  
  return token;
};

// Helper for authenticated requests
const authRequest = async (method, endpoint, data = null, config = {}) => {
  try {
    // Get the current authentication token
    const token = getAuthToken();
    
    if (!token) {
      console.error('No authentication token available');
      throw new Error('Authentication required. Please log in.');
    }
    
    // Create default headers with authentication
    const headers = {
      ...config.headers,
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    // If there's a tenant ID in localStorage, include it in the headers
    const tenantId = localStorage.getItem('tenantId');
    if (tenantId) {
      headers['x-tenant-id'] = tenantId;
    }
    
    // Add user ID if available
    const userId = localStorage.getItem('userId');
    if (userId) {
      headers['x-user-id'] = userId;
    }

    // Create the request configuration
    const requestConfig = {
      ...config,
      headers,
      method,
      url: `${API_URL}${endpoint}`
    };
    
    // Add data to the request if provided
    if (data) {
      if (method.toLowerCase() === 'get') {
        requestConfig.params = data;
      } else {
        requestConfig.data = data;
      }
    }

    console.log(`Making ${method} request to ${endpoint} with headers:`, {
      Authorization: 'Bearer <token>',
      'x-tenant-id': tenantId || 'Not provided',
      'Content-Type': headers['Content-Type']
    });

    // Make the request
    const response = await axios(requestConfig);
    return response.data;
  } catch (error) {
    console.log(' Error in', method.toLowerCase(), 'request to', endpoint + ':', error);
    
    // Log more details about the error for debugging
    if (error.response) {
      console.log(' Response data:', error.response.data);
      console.log(' Response status:', error.response.status);
      console.log(' Response headers:', error.response.headers);
    } else {
      console.log(' No response received from server');
    }
    
    // Handle token expiration
    if (error.response && error.response.status === 401) {
      // Check if the error message indicates token expiration
      const errorMsg = error.response.data?.message || '';
      if (errorMsg.includes('expired') || errorMsg.includes('invalid') || errorMsg.includes('no authentication token')) {
        console.log('Session expired or invalid. Redirecting to login...');
        // Clear auth storage
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        // Redirect to login page
        window.location.href = '/login?expired=true';
        return;
      }
    }
    
    throw error;
  }
};

// Create a workflow request with attachments using multipart/form-data
const createWorkflowRequestWithAttachments = async (formData, onUploadProgress = null) => {
  try {
    // Get the current authentication token
    const token = getAuthToken();
    
    if (!token) {
      console.error('No authentication token available');
      throw new Error('Authentication required. Please log in.');
    }
    
    // Create headers for multipart/form-data (don't set Content-Type, axios will set it with boundary)
    const headers = {
      'Authorization': `Bearer ${token}`
    };
    
    // If there's a tenant ID in localStorage, include it in the headers
    const tenantId = localStorage.getItem('tenantId');
    if (tenantId) {
      headers['x-tenant-id'] = tenantId;
    }
    
    // Add user ID if available
    const userId = localStorage.getItem('userId');
    if (userId) {
      headers['x-user-id'] = userId;
    }
    
    console.log('Making file upload request with headers:', {
      Authorization: 'Bearer <token>',
      'x-tenant-id': tenantId || 'Not provided',
    });
    
    // Configure the request
    const config = {
      headers,
      method: 'post',
      url: `${API_URL}/workflows`,
      data: formData,
      onUploadProgress: onUploadProgress
    };
    
    // Make the request
    const response = await axios(config);
    return response.data;
  } catch (error) {
    console.log('Error in post request to /api/workflows:', error);
    
    // Log more details about the error for debugging
    if (error.response) {
      console.log('Response data:', error.response.data);
      console.log('Response status:', error.response.status);
      console.log('Response headers:', error.response.headers);
    } else {
      console.log('No response received from server');
    }
    
    // Handle token expiration
    if (error.response && error.response.status === 401) {
      // Check if the error message indicates token expiration
      const errorMsg = error.response.data?.message || '';
      if (errorMsg.includes('expired') || errorMsg.includes('invalid') || errorMsg.includes('no authentication token')) {
        console.log('Session expired or invalid. Redirecting to login...');
        // Clear auth storage
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        // Redirect to login page
        window.location.href = '/login?expired=true';
        return;
      }
    }
    
    throw error;
  }
};

// Get workflow templates
const getWorkflowTemplates = () => {
  return authRequest('get', '/workflows/templates');
};

// Get template by ID
const getWorkflowTemplateById = (templateId) => {
  return authRequest('get', `/workflows/templates/${templateId}`);
};

// Create workflow template
const createWorkflowTemplate = (templateData) => {
  return authRequest('post', '/workflows/templates', templateData);
};

// Update workflow template
const updateWorkflowTemplate = (templateId, templateData) => {
  return authRequest('put', `/workflows/templates/${templateId}`, templateData);
};

// Delete workflow template
const deleteWorkflowTemplate = (templateId) => {
  return authRequest('delete', `/workflows/templates/${templateId}`);
};

// Get workflow requests for the current logged-in member
const getMemberRequests = () => {
  return authRequest('get', '/workflows/member');
};

// Get workflow request by ID
const getRequestById = (requestId) => {
  return authRequest('get', `/workflows/${requestId}`);
};

// Get workflow request by ID (for member)
const getMemberRequestById = (requestId) => {
  return authRequest('get', `/workflows/member/${requestId}`);
};

// Get workflow request by ID (admin version)
const getAdminRequestById = (requestId) => {
  return authRequest('get', `/workflows/${requestId}`);
};

// Add comment to workflow request
const addComment = (requestId, comment) => {
  return authRequest('post', `/workflows/${requestId}/comments`, { text: comment });
};

// Add comment to workflow request as a member
const addMemberComment = (requestId, comment) => {
  return authRequest('post', `/workflows/member/${requestId}/comments`, { text: comment });
};

// Perform action on workflow request
const performAction = async (requestId, actionId, comment) => {
  try {
    // Check if we have valid parameters
    if (!requestId || !actionId) {
      throw new Error('Missing required parameters: requestId or actionId');
    }
    
    // Log the actual action ID being used for debugging purposes
    console.log(`Performing action with ID "${actionId}" on request ${requestId}`);
    
    // Create the request body
    const requestBody = {
      comment: comment || ''
    };
    
    // Make sure actionId is a string and not an object
    const actionIdStr = typeof actionId === 'object' ? actionId.id : actionId;
    
    // Log what we're actually sending to the server
    console.log(`Making POST request to /workflows/${requestId}/actions/${actionIdStr}`);
    
    // Make a request to the correct endpoint
    const response = await authRequest(
      'post',
      `/workflows/${requestId}/actions/${actionIdStr}`,
      requestBody
    );
    
    console.log('Action performed successfully. Response:', response);
    
    // Ensure we're returning the complete response object
    return response;
  } catch (error) {
    console.error('Error performing workflow action:', error);
    
    // Enhanced error logging to help diagnose issues
    if (error.response) {
      console.error('Server response status:', error.response.status);
      console.error('Server response data:', error.response.data);
      
      // If we got a 404 for the action not found, provide more helpful information
      if (error.response.status === 404 && error.response.data?.message?.includes('Action not found')) {
        console.error('The action ID was not found in the current workflow step.');
        
        if (error.response.data?.availableActions) {
          // Log available actions in a readable format
          console.error('Available actions:');
          
          if (error.response.data.availableActions.admin) {
            console.error('  Admin actions:');
            error.response.data.availableActions.admin.forEach(action => {
              console.error(`    - ${action.label} (ID: ${action.id}, Type: ${action.type})`);
            });
          }
          
          if (error.response.data.availableActions.member) {
            console.error('  Member actions:');
            error.response.data.availableActions.member.forEach(action => {
              console.error(`    - ${action.label} (ID: ${action.id}, Type: ${action.type})`);
            });
          }
          
          if (error.response.data.availableActions.byType) {
            console.error('  Actions by type:');
            for (const [type, actions] of Object.entries(error.response.data.availableActions.byType)) {
              if (actions.length > 0) {
                console.error(`    ${type}: ${actions.map(a => a.id).join(', ')}`);
              }
            }
          }
        }
      }
    }
    
    throw error;
  }
};

// Perform action on workflow request as a member
const performMemberAction = (requestId, actionId, comment) => {
  console.log(`Performing member action: ${actionId} on request: ${requestId}`);
  return authRequest('post', `/workflows/member/${requestId}/actions/${actionId}`, { 
    comment,
    actionId // Explicitly include the actionId in the request body as well
  });
};

// Get all workflow requests (admin only)
const getAllRequests = (page = 1, limit = 10, status = '', searchTerm = '') => {
  // Only include parameters that have actual values
  const params = {};
  if (page) params.page = page;
  if (limit) params.limit = limit;
  if (status) params.status = status;
  if (searchTerm) params.search = searchTerm;
  
  return authRequest('get', '/workflows/admin', params);
};

// Get request counts
const getRequestCounts = () => {
  return authRequest('get', '/workflows/counts');
};

// Get workflow templates for admin/staff view
const getAdminTemplates = async () => {
  try {
    // Try the admin endpoint first
    try {
      const response = await axiosInstance.get('/workflows/templates/admin');
      return response;
    } catch (error) {
      // If 403 error, fallback to the regular templates endpoint
      if (error.response && error.response.status === 403) {
        console.log('Falling back to regular templates endpoint');
        const response = await axiosInstance.get('/workflows/templates');
        return response;
      }
      // Otherwise rethrow the error
      throw error;
    }
  } catch (error) {
    console.error('Error fetching workflow templates:', error);
    throw error;
  }
};

// Get staff templates - use the same endpoint as getAdminTemplates but with better error handling
const getStaffTemplates = async () => {
  try {
    // Try normal templates endpoint first (which allows staff)
    const response = await axiosInstance.get('/workflows/templates');
    return response;
  } catch (error) {
    console.error('Error fetching staff workflow templates:', error);
    throw error;
  }
};

// Add/update the getAttachmentUrl method
const getAttachmentUrl = (filename) => {
  if (!filename) return '';
  
  // For direct access without authentication
  return `${API_URL}/uploads/workflow-attachments/${filename}`;
  
  // Alternative approach with authentication (if needed)
  // const token = getAuthToken();
  // return `${API_URL}/uploads/workflow-attachments/${filename}?token=${token}`;
};

// Use a staff-specific endpoint that works properly with staff role permissions
const getStaffRequests = (page = 1, limit = 10, status = '', searchTerm = '') => {
  // Only include parameters that have actual values
  const params = {};
  if (page) params.page = page;
  if (limit) params.limit = limit;
  if (status) params.status = status;
  if (searchTerm) params.search = searchTerm;
  
  return authRequest('get', '/workflows/staff', params);
};

// Get request by ID for staff users
const getStaffRequestById = (requestId) => {
  return authRequest('get', `/workflows/staff/${requestId}`);
};

// Get workflow stats for dashboard - fallback to existing methods
const getWorkflowStats = async (timeRange) => {
  // Use the existing getRequestCounts method and enhance it
  try {
    const response = await getRequestCounts();
    
    // Format response data for the dashboard
    const statsData = {
      total: response?.data?.total || 0,
      active: response?.data?.active || 0,
      completed: response?.data?.completed || 0,
      pendingReview: 0,
      rejected: 0,
      trend: 'up',  // Default trend
      trendPercentage: 5 // Default percentage
    };
    
    // Calculate pendingReview and rejected counts from statuses
    if (response?.data?.statuses) {
      Object.entries(response.data.statuses).forEach(([status, count]) => {
        const statusLower = status.toLowerCase();
        if (statusLower.includes('pending') || statusLower.includes('review')) {
          statsData.pendingReview += count;
        } else if (statusLower.includes('reject') || statusLower.includes('denied')) {
          statsData.rejected += count;
        }
      });
    }
    
    return { data: statsData };
  } catch (error) {
    console.error('Error getting workflow stats:', error);
    throw error;
  }
};

// Get recent workflows for dashboard
const getRecentWorkflows = async (timeRange) => {
  // Use existing getAllRequests or getStaffRequests based on user role
  try {
    // Detect if we're in a staff context using pathname
    const isStaffContext = window.location.pathname.startsWith('/staff/');
    
    const response = isStaffContext
      ? await getStaffRequests(1, 10)
      : await getAllRequests(1, 10);
    
    // Format data to match what the dashboard expects
    const formattedWorkflows = response?.data?.map(workflow => ({
      id: workflow._id,
      title: workflow.title,
      type: workflow.workflowTemplate?.name || 'Custom Workflow',
      status: workflow.currentStatus || 'Submitted',
      requester: workflow.member?.userId?.firstName 
        ? `${workflow.member.userId.firstName} ${workflow.member.userId.lastName || ''}`
        : 'Unknown',
      lastUpdated: new Date(workflow.updatedAt).toLocaleDateString(),
      dueDate: '', // No due date in current schema
      step: workflow.currentStep?.name || 'Unknown'
    })) || [];
    
    return { data: formattedWorkflows };
  } catch (error) {
    console.error('Error getting recent workflows:', error);
    throw error;
  }
};

const workflowService = {
  getWorkflowTemplates,
  getWorkflowTemplateById,
  createWorkflowTemplate,
  updateWorkflowTemplate,
  deleteWorkflowTemplate,
  createWorkflowRequestWithAttachments,
  getMemberRequests,
  getRequestById,
  getMemberRequestById,
  getAdminRequestById,
  addComment,
  addMemberComment,
  performAction,
  performMemberAction,
  getAllRequests,
  getRequestCounts,
  getAdminTemplates,
  getStaffTemplates,
  getAttachmentUrl,
  addAttachmentsToWorkflow: async (requestId, formData, onUploadProgress) => {
    try {
      // Get the current authentication token
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No authentication token available');
        throw new Error('Authentication required. Please log in.');
      }
      
      // Create headers for multipart/form-data
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      
      // If there's a tenant ID in localStorage, include it in the headers
      const tenantId = localStorage.getItem('tenantId');
      if (tenantId) {
        headers['x-tenant-id'] = tenantId;
      }
      
      console.log(`Making attachment upload request to workflow ${requestId}`);
      
      // Configure the request - Fixed endpoint URL to match backend implementation
      const config = {
        headers,
        method: 'post',
        url: `${API_URL}/workflows/attachments/${requestId}`,
        data: formData,
        onUploadProgress: onUploadProgress
      };
      
      // Make the request
      const response = await axios(config);
      return response;
    } catch (error) {
      console.error('Error in attachment upload:', error);
      throw error;
    }
  },
  getStaffRequests,
  getStaffRequestById,
  getWorkflowStats,
  getRecentWorkflows,
};

export default workflowService;
